import { debounceTime, filter, map, take } from 'rxjs/operators';
import { ArticleVO } from '@article/model/vo/article.vo';
import { lastValueFrom, Subscription } from 'rxjs';
import { OverlayService } from '../overlay/overlay.service';
import { StringUtils } from '@shared/util/string-utils';
import { QueryUtils } from '../util/query.utils';
import { ConfiguratorFunction } from '@constants/configurator-function';
import { NumberUtils } from '@shared/util/number-utils';
import { AlertHelper } from '../alert/alert.helper';
import { UserModel } from '@user/model/user.model';
import { UserUtils } from '@user/service/user-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { UserProfileUtils } from '@user/service/user-profile-utils';
import { AlertButton } from '@shared/alert/alertButton';
import { ProjectUtils } from '@project/components/project/util/project-utils';
import { ProjectSaveInfoVO } from '@project/model/vo/project.vo';
import { DateFormatter } from '../date-formatter';
import { Injectable } from '@angular/core';
import { RouteNavigationService } from '@services/route-navigation.service';
import { Router, RouterEvent } from '@angular/router';
import { CookieStorage } from '../cookie-storage';
import { OverlayBackground } from '../overlay/overlay.type';
import { ArticleDataService } from '@article/service/article-data.service';
import { ArticleNosToHeadMappingVO } from '@article/model/vo/articlesnos-to-head-mapping';
import { UserActionsLoggingManager } from '@user/service/logging/user-actions-logging.manager';
import { LoadProjectService } from '@project/service/load-project.service';
import { CreateProjectService } from '@project/service/create-project.service';
import { CollectionService } from '@user/components/favorites/service/collection.service';
import { Log } from '@shared/debug/log';
import { UserComponentPresenter } from '@user/components/user-component.presenter';
import { ProjectDataService } from '@project/service/project-data.service';
import { ProjectService } from '@project/components/import-export/service/project.service';
import { IEKAssistantManager } from '@project/components/article/manager/iek-assistant.manager';
import { ProjectNavigationPresenter } from '@project-navigation/project-navigation.presenter';
import { CaeImportService } from '@project/cae/cae-import.service';

@Injectable({providedIn: 'root'})
export class QueryParamsManager {
  private articleQueueForAddingToCollection: ArticleVO[] = [];
  private isArticlePopupOpened: boolean = false;
  public isConfiguratorActiveSubscription?: Subscription;

  private _eShopTransferHandler = (event: MessageEvent) => this.receiveMessage(event);

  constructor(
    private _cookieStorage: CookieStorage,
    private router: Router,
    private userActionsLoggingManager: UserActionsLoggingManager,
    private userModel: UserModel,
    private overlayService: OverlayService,
    private projectDataService: ProjectDataService,
    private alertHelper: AlertHelper,
    private projectService: ProjectService,
    private loadProjectService: LoadProjectService,
    private routeNavigationService: RouteNavigationService,
    private articleDataService: ArticleDataService,
    private createProjectService: CreateProjectService,
    private collectionService: CollectionService,
    private _userComponentPresenter: UserComponentPresenter,
    private iekAssistantManager: IEKAssistantManager,
    private projectNavigationPresenter: ProjectNavigationPresenter,
    private caeImportService: CaeImportService,
  ) {
  }

  public createEShopTransferHandler(): void {
    window.name = 'wsd';
    (<any>window).sdloaded = true;
    window.addEventListener('message', this._eShopTransferHandler, false);
  }

  private async receiveMessage(event: MessageEvent): Promise<void> {
    if (!event || !event.data || !event.data.smartDesignerEvent) {
      return;
    }
    const origin = event.origin;
    const currentDomain = this.getCurrentDomainURL();
    if (origin === currentDomain) {
      // currently we only have single supported message for 'createproject'
      const articleNo: string = StringUtils.replaceAll(event.data.articleIds, '_', '/');
      // In case there is no articleNo, we simply ignore creating such project, and return false because of such invalid calls.
      // Ivalid calls might happen, in case some message in html page passes any previous checks and triggers call to this method
      // or in case some javascript code added to page (other than Google Analytics) calls this method (both through ExternalInterface).
      if (event.data.func === 'createproject' && StringUtils.isNotEmpty(articleNo)) {
        const articlesnosToArticleHeadsMap = await lastValueFrom(this.articleDataService.mapArticleNosToArticleHeads([articleNo]));
        if (!this.overlayService.getCurrentState()) {
          await this.createNewProject(articlesnosToArticleHeadsMap);
        } else {
          await lastValueFrom(this.overlayService.stateChange.pipe(debounceTime(200), take(1)))
          if (!this.overlayService.getCurrentState()) {
            await this.createNewProject(articlesnosToArticleHeadsMap);
          }
        }
      }
    }
  }

  private async createNewProject(articlesnosToArticleHeadsMap: ArticleNosToHeadMappingVO): Promise<void> {
    if (!this.isNotSpecialArticleType(articlesnosToArticleHeadsMap) || this.routeNavigationService.isAtConfigurator()) {
      await this.executeCreateNewProject(articlesnosToArticleHeadsMap);
    } else {
      const article = articlesnosToArticleHeadsMap.articles[0];
      if (!this.articleQueueForAddingToCollection.find((a) => a.identity === article.identity)) {
        this.articleQueueForAddingToCollection.push(article);
      }
      if (!this.isConfiguratorActiveSubscription) {
        this.isConfiguratorActiveSubscription = this.router.events
          .pipe(
            filter<any>((event) => event instanceof RouterEvent),
            map((event: RouterEvent) => {
              if (event.url.includes('/configurator')) {
                this.isConfiguratorActiveSubscription?.unsubscribe();
                this.openMultipleArticleCollectionPopup();
              }
            })
          )
          .subscribe();
      }
    }
  }

  private getCurrentDomainURL(): string {
    return window.location.protocol + '//' + window.location.hostname + (window.location.port ? ':' + window.location.port : '');
  }

  public async handleQueryParams(): Promise<boolean> {
    // Load project from URL or import projects from CAE by storageId
    const functionParamValue = QueryUtils.getParameterByName('function')?.toLowerCase() as ConfiguratorFunction;

    if (!functionParamValue) {
      return false;
    }

    switch (functionParamValue) {
      case "finalizeproject":
        await this.handleFunctionFinalizeProject();
        return true;
      case "loadproject":
        await this.handleFunctionLoadProject();
        return true;
      case "createproject":
        await this.handleFunctionCreateProject();
        return true;
      case "caeimport":
        this.handleFunctionCaeImport();
        return true;
      case "assistantimport": {
        await this.handleFunctionAssistantImport();
        return true;
      }
      default:
        return false;
    }
  }


  private async handleFunctionAssistantImport() {
    const assistantId: string = QueryUtils.getParameterByName('assistantname');
    this.userActionsLoggingManager.logFunction('function.assistant_import', assistantId);
    const storageId: string = QueryUtils.getParameterByName('storageid');
    if (storageId) {
      await this.startCreateAssistantsProject(storageId);
    }
  }

  private handleFunctionCaeImport() {
    this.userActionsLoggingManager.logFunction('function.cae_import');
    const storageId: string = QueryUtils.getParameterByName('storageid');
    if (storageId) {
      this.caeImportService.startCaeImportWithStorageId(storageId);
    }
  }

  private async handleFunctionCreateProject() {
    const articleIdsParamValue: string = QueryUtils.getParameterByName('articleids');
    this.userActionsLoggingManager.logFunction('function.create_project', articleIdsParamValue);
    let articleNos: string[] = articleIdsParamValue.split(';');
    articleNos = articleNos.map((articleNo) => (articleNo ? StringUtils.replaceAll(articleNo, '_', '/') : ""));

    const articlesnosToArticleHeadsMap = await lastValueFrom(this.articleDataService.mapArticleNosToArticleHeads(articleNos));
    if (!this.userModel.isUserLoggedIn) {
      await this._userComponentPresenter.presentReloginComponent(true, false, true);
    }
    await this.executeCreateNewProject(articlesnosToArticleHeadsMap);
  }

  private async handleFunctionLoadProject() {
    this.userActionsLoggingManager.logFunction('function.load_project');
    const guidParamValue: string = QueryUtils.getParameterByName('guid');
    if (guidParamValue) {
      await this.loadProjectByGuidFunctionHandler(guidParamValue);
    } else {
      await this.loadProjectFunctionHandler();
    }
  }

  private async handleFunctionFinalizeProject() {
    this.userActionsLoggingManager.logFunction('function.finalize_project');
    await this.loadProjectFunctionHandler();
  }

  private isNotSpecialArticleType(articlenosToHeadMapping: ArticleNosToHeadMappingVO): boolean {
    if (articlenosToHeadMapping.articles.length > 1) {
      return true;
    }
    return !(
      articlenosToHeadMapping.isFirstArticleGDSArticle ||
      articlenosToHeadMapping.isFirstArticleMCSArticle ||
      articlenosToHeadMapping.isFirstArticleMarkingSystemArticle
    );
  }

  private async executeCreateNewProject(articlenosToHeadMapping: ArticleNosToHeadMappingVO): Promise<void> {
    if (!articlenosToHeadMapping) {
      return;
    }
    if (articlenosToHeadMapping.nonExistingArticleNos.length > 0) {
      await this.alertHelper
        .showError('initManager.title.unknown_article_to_create_project', 'initManager.message.unknown_article_to_create_project', {
          translationParams: [articlenosToHeadMapping.nonExistingArticleNos.join(", ")],
        });
    }
    if (this.isNotSpecialArticleType(articlenosToHeadMapping)) {
      this.executeAddArticlesToCollection(articlenosToHeadMapping);
      return;
    }
    if (!this.userModel.user.hasPermission("EDIT_PROJECT")) {
      this.alertHelper.showError('serverCommunication.title.user_has_no_rights', 'serverCommunication.message.user_has_no_rights');
      return;
    }
    if (articlenosToHeadMapping.isFirstArticleMCSArticle) {
      if (!UserUtils.hasCreateMcsGdsProjectsPermission(this.userModel.user)) {
        this.alertHelper.showError('serverCommunication.title.user_has_no_rights', 'serverCommunication.message.user_has_no_rights');
        return;
      }
      await this.startCreateNewProject(articlenosToHeadMapping);
    } else if (articlenosToHeadMapping.isFirstArticleGDSArticle) {
      if (!UserUtils.hasCreateMcsGdsProjectsPermission(this.userModel.user)) {
        this.alertHelper.showError('serverCommunication.title.user_has_no_rights', 'serverCommunication.message.user_has_no_rights');
        return;
      }
      await this.startCreateNewProject(articlenosToHeadMapping);
    } else if (articlenosToHeadMapping.isFirstArticleMarkingSystemArticle) {
      await this.startCreateNewProject(articlenosToHeadMapping);
    }
  }

  private executeAddArticlesToCollection(articlenosToHeadMapping: ArticleNosToHeadMappingVO): void {
    articlenosToHeadMapping.articles.forEach((articlenosToHead) => {
      if (!this.articleQueueForAddingToCollection.find((a) => a.identity === articlenosToHead.identity)) {
        this.articleQueueForAddingToCollection.push(articlenosToHead);
      }
    });
    if (!this.isArticlePopupOpened) {
      this.openMultipleArticleCollectionPopup();
    }
  }

  private async openMultipleArticleCollectionPopup(): Promise<void> {
    if (ArrayUtils.isNotEmpty(this.articleQueueForAddingToCollection)) {
      const articleCollection =
        await this._userComponentPresenter.presentAddMultipleArticlesToArticleCollectionPopup(this.articleQueueForAddingToCollection,
          UserProfileUtils.getAllAvailableArticleCollections(this.userModel.userProfile, this.articleQueueForAddingToCollection));
      if (articleCollection) {
        this.collectionService.notifySingleCollectionChanged(articleCollection.id);
      }
      this.isArticlePopupOpened = true;
    } else {
      this.isArticlePopupOpened = false;
    }
  }

  private async startCreateNewProject(articlenosToHeadMapping: ArticleNosToHeadMappingVO): Promise<void> {
    const article: ArticleVO = articlenosToHeadMapping.articles[0];
    if (articlenosToHeadMapping.isFirstArticleGDSArticle) {
      await this.createProjectService.createGdsProject(article);
    } else if (articlenosToHeadMapping.isFirstArticleMCSArticle) {
      await this.createProjectService.createMcsProject(article);
    } else if (articlenosToHeadMapping.isFirstArticleMarkingSystemArticle) {
      await this.createProjectService.createMarkingSystemProject(article);
    }
  }

  private async startCreateAssistantsProject(guid: string): Promise<void> {
    if (this._cookieStorage.userConfirmedIEKAssistantMessage && this.userModel.userProfile.isRegisteredUserAccount()) {
      await this.continueCreateAssistantsProject(guid);
    } else {
      await this._userComponentPresenter.presentReloginActionComponent(
        'relogin_action.iekAssistant.title',
        'relogin_action.iekAssistant.infoMessage');
      this._cookieStorage.userConfirmedIEKAssistantMessage = true;
      await this.continueCreateAssistantsProject(guid);
    }
  }

  private async continueCreateAssistantsProject(guid: string): Promise<void> {
    return this.iekAssistantManager.createAssistantsProject(guid);
  }

  private async loadProjectByGuidFunctionHandler(guid: string): Promise<void> {
    try {
      await this.loadProjectService.loadProjectByGuid(guid);
      this.overlayService.requestOverlayHide(OverlayBackground.color);
    } catch (error) {
      Log.error('Could not load project by guid: ', error);
      this.overlayService.requestOverlayHide(OverlayBackground.color);
      const projectIdParamValue: number = parseFloat(QueryUtils.getParameterByName('projectid', this.router.url));
      if (NumberUtils.isNotNumber(projectIdParamValue)) {
        this.loadProjectFunctionErrorHandler();
      } else {
        await this.loadProjectWithExpiredOrInvalidGuid();
      }
    }
  }

  private async loadProjectWithExpiredOrInvalidGuid(): Promise<void> {
    if (this.userModel.isUserLoggedIn) {
      return await this.loadProjectFunctionHandler(this.router.url);
    }

    const alertTitle: string = 'load.title.project_link_expired';
    const alertMessage: string = 'load.message.project_link_expired';
    const answer = await this.alertHelper
      .showAlert(alertTitle, alertMessage, undefined, [AlertButton.CANCEL, AlertButton.YES], undefined, {yesLabel: 'login.login_part.login_button.label'});

    if (answer === AlertButton.YES) {
      await this._userComponentPresenter.presentReloginComponent(true, false, true);
      if (this.userModel.isUserLoggedIn) {
        await this.loadProjectFunctionHandler(this.router.url);
      }
    }
  }

  private async loadProjectFunctionHandler(url?: string): Promise<void> {
    const projectIdParamValue: number = parseFloat(QueryUtils.getParameterByName('projectid', url));
    if (NumberUtils.isNumber(projectIdParamValue)) {
      const projectVersionParamValue: number = parseFloat(QueryUtils.getParameterByName('projectversion', url));
      if (NumberUtils.isNotNumber(projectVersionParamValue)) {
        // there is no correct working parsing method for requested date&time format in flex, thus sending string to server and parse it there
        const dateParamValue: string = QueryUtils.getParameterByName('date', url);
        if (StringUtils.isEmpty(dateParamValue)) {
          // To get locked status of project:
          try {
            const result = await lastValueFrom(this.projectDataService.loadProjectSaveInfo(projectIdParamValue, -1, undefined, false));
            await this.checkLatestProjectVersionToLoadProject_success(projectIdParamValue, result);
          } catch (error) {
            Log.error('Could not load project save info: ', error);
            this.loadProjectFunctionErrorHandler();
          }
        } else {
          try {
            const projectSaveInfo = await lastValueFrom(this.projectDataService.loadProjectSaveInfo(projectIdParamValue, -1, dateParamValue, false));
            await this.checkLatestProjectVersionToLoadProject_success(projectIdParamValue, projectSaveInfo);
          } catch (error) {
            Log.error('Could not load project save info: ', error);
            this.loadProjectFunctionErrorHandler();
          }
        }
      } else {
        try {
          const result = await lastValueFrom(this.projectDataService.loadProjectSaveInfo(projectIdParamValue, projectVersionParamValue, undefined, false));
          await this.checkLatestProjectVersionToLoadProject_success(projectIdParamValue, result);
        } catch (error) {
          Log.error('Could not load project save info: ', error);
          this.loadProjectFunctionErrorHandler();
        }
      }
    }
  }

  private loadProjectFunctionErrorHandler(): void {
    this.alertHelper.showError('load.title.project_link_invalid_or_expired', 'load.message.project_link_invalid_or_expired');
  }

  private async checkLatestProjectVersionToLoadProject_success(projectId: number, projectSaveInfo: ProjectSaveInfoVO): Promise<void> {
    if (NumberUtils.isNotNumber(projectId) || !projectSaveInfo) {
      return;
    }
    const isProductionUser: boolean = this.userModel.user && this.userModel.user.isProduction();
    if (
      projectSaveInfo &&
      projectSaveInfo.projectWithHighestVersion &&
      projectSaveInfo.projectWithHighestVersion.isLocked &&
      UserUtils.isWagoUser(this.userModel.user)
    ) {
      await this.projectService.showProjectLockedMessage(isProductionUser);
      if (isProductionUser) {
        await this.projectNavigationPresenter.openLoadProjectDialog();
      } else {
        await this.continueCheckLatestProjectVersionToLoadProject(projectSaveInfo, projectId, isProductionUser);
      }
    } else {
      await this.continueCheckLatestProjectVersionToLoadProject(projectSaveInfo, projectId, isProductionUser);
    }
  }


  private async continueCheckLatestProjectVersionToLoadProject(
    projectSaveInfo: ProjectSaveInfoVO,
    requestedProjectId: number,
    isProductionUser: boolean
  ): Promise<void> {
    const requestedProjectVersion: number = projectSaveInfo.requestedProjectVersion;
    const requestedProjectVersionSaveDate: Date = projectSaveInfo.requestedProjectVersionSaveDate;
    const requestedProjectVersionFound: boolean = projectSaveInfo.requestedProjectVersionFound;
    const highestFinalizedProjectVersion: number = projectSaveInfo.highestFinalizedProjectVersion;
    const newerVersionAvailable: boolean = requestedProjectVersion !== highestFinalizedProjectVersion && highestFinalizedProjectVersion !== -1;

    const latestProjectVersion = projectSaveInfo?.projectWithHighestVersion?.currentVersion ?? -1;
    const latestProjectVersionDisplayStringWithDate: string = ProjectUtils.getVersionDisplayString(projectSaveInfo?.projectWithHighestVersion, true) ?? "";
    const executeLoadProject = !projectSaveInfo?.projectWithHighestVersion || (requestedProjectVersionFound && latestProjectVersion === requestedProjectVersion && !isProductionUser);

    if (executeLoadProject || (isProductionUser && highestFinalizedProjectVersion !== -1 && !newerVersionAvailable)) {
      await this.loadProjectService.loadProjectById(requestedProjectId, requestedProjectVersion);
      return;
    }

    const answer = await this.showLoadOtherVersionAlert({
      requestedProjectVersionFound,
      requestedProjectVersion,
      isProductionUser,
      highestFinalizedProjectVersion,
      requestedProjectVersionSaveDate,
      latestProjectVersionDisplayStringWithDate,
      newerVersionAvailable,projectSaveInfo,
    });

    if (answer === AlertButton.YES) {
      const version = isProductionUser ? highestFinalizedProjectVersion : latestProjectVersion;
      await this.loadProjectService.loadProjectById(requestedProjectId, version);
    } else if (answer === AlertButton.OK) {
      await this.projectNavigationPresenter.openLoadProjectDialog();
    } else {
      if (newerVersionAvailable && isProductionUser) {
        await this.projectNavigationPresenter.openLoadProjectDialog();
      } else if (requestedProjectVersionFound) {
        await this.loadProjectService.loadProjectById(requestedProjectId, requestedProjectVersion);
      }
    }
  }


  private async showLoadOtherVersionAlert(
    alertConditions: {
      requestedProjectVersionFound: boolean,
      requestedProjectVersion: number,
      isProductionUser: boolean,
      highestFinalizedProjectVersion: number,
      requestedProjectVersionSaveDate: Date,
      latestProjectVersionDisplayStringWithDate: string,
      newerVersionAvailable: boolean,
      projectSaveInfo: ProjectSaveInfoVO
    }
  ): Promise<AlertButton> {
    let alertMessageKey: string = "";
    let alertTitleKey: string = "";
    let alertMessageParameters: any;

    if (!alertConditions.requestedProjectVersionFound && alertConditions.requestedProjectVersion === -1) {
      // project not found by provided project save date in link
      alertMessageKey = 'load.message.project_version_save_date_not_found';
      alertTitleKey = 'load.title.project_version_save_date_not_found';
      alertMessageParameters = {
        0: !alertConditions.requestedProjectVersionSaveDate ? '--' : DateFormatter.formatDate(alertConditions.requestedProjectVersionSaveDate),
        1: alertConditions.latestProjectVersionDisplayStringWithDate,
      };
    } else if (!alertConditions.requestedProjectVersionFound) {
      // project not found by provided project version in link, we are using old project version letter in such case as we cannot determine user defined version of not existing (not found) project
      alertMessageKey = 'load.message.project_version_not_found';
      alertTitleKey = 'load.title.project_version_not_found';
      alertMessageParameters = {0: ProjectUtils.getVersionAsLetter(alertConditions.requestedProjectVersion), 1: alertConditions.latestProjectVersionDisplayStringWithDate};
    } else {
      if (alertConditions.isProductionUser) {
        if (alertConditions.highestFinalizedProjectVersion === -1) {
          alertMessageKey = 'load.message.project_not_released';
          alertTitleKey = 'load.title.project_not_released';
        } else if (alertConditions.newerVersionAvailable) {
          alertMessageKey = 'load.message.newer_production_version_available';
          alertTitleKey = 'load.title.newer_project_version_available';
          alertMessageParameters = {
            0: alertConditions.projectSaveInfo.requestedProjectDocumentVersion,
            1: alertConditions.projectSaveInfo.projectWithHighestVersion.documentVersion,
          };
        }
      } else {
        const requestedProjectVersionDisplayStringWithDate: string = ProjectUtils.getRequestedVersionDisplayStringFromProjectSaveInfo(
          alertConditions.projectSaveInfo,
          true
        );
        alertMessageKey = 'load.message.newer_project_version_available';
        alertTitleKey = 'load.title.newer_project_version_available';
        alertMessageParameters = {0: requestedProjectVersionDisplayStringWithDate, 1: alertConditions.latestProjectVersionDisplayStringWithDate};
      }
    }

    return await this.alertHelper
      .showAlert(
        alertTitleKey,
        alertMessageKey,
        alertMessageParameters,
        alertConditions.isProductionUser && !alertConditions.newerVersionAvailable ? [AlertButton.OK] : [AlertButton.YES, AlertButton.NO],
        "info"
      );
  }
}
